<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <b-card>
        <h4><i class="fas fa-bullhorn"></i> รายงานช่องทางนัดหมาย</h4>

        <div class="report-page">
          <div class="filter-card">
            <div class="asr-row">
              <div class="field asr-date">
                <label class="field-lbl">ช่วงวันที่</label>
                <date-picker
                  v-model="dateRange"
                  type="date"
                  range
                  format="DD MMM YY"
                  :shortcuts="dateRangeShortcuts"
                  placeholder="เลือกวันที่ต้องการค้นหา"
                  input-class="form-control form-control-sm field-input-native"
                ></date-picker>
              </div>

              <div class="field">
                <span class="field-lbl">นับตาม</span>
                <div class="seg" role="group" aria-label="นับตาม">
                  <button
                    v-for="o in dateFieldOptions"
                    :key="o.value"
                    type="button"
                    class="seg-btn"
                    :class="{ active: dateField === o.value }"
                    :aria-pressed="String(dateField === o.value)"
                    @click="dateField = o.value"
                  >
                    {{ o.text }}
                  </button>
                </div>
              </div>

              <div class="field">
                <span class="field-lbl">แสดงเป็น</span>
                <div class="seg" role="group" aria-label="แสดงเป็น">
                  <button
                    v-for="o in groupByOptions"
                    :key="o.value"
                    type="button"
                    class="seg-btn"
                    :class="{ active: groupBy === o.value }"
                    :aria-pressed="String(groupBy === o.value)"
                    @click="groupBy = o.value"
                  >
                    {{ o.text }}
                  </button>
                </div>
              </div>

              <div class="asr-actions">
                <b-dropdown
                  size="sm"
                  variant="outline-success"
                  right
                  :disabled="!report || isExporting"
                >
                  <template #button-content>
                    <i class="fas fa-download mr-1"></i>ส่งออก
                  </template>
                  <b-dropdown-item @click="exportExcel">
                    <i class="fas fa-file-excel mr-2"></i>Excel
                  </b-dropdown-item>
                  <b-dropdown-item @click="exportPdf">
                    <i class="fas fa-file-pdf mr-2"></i>PDF
                  </b-dropdown-item>
                </b-dropdown>
                <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">
                  แสดงข้อมูล
                </b-button>
              </div>
            </div>

            <div class="fc-row fc-row-multi">
              <ClinicBranchMultiSelect v-model="branchIds" :options="branchOptions" />
              <div class="field clinic-multi-field">
                <label class="field-lbl">
                  ผู้สร้างนัด
                  <span v-if="creatorUids.length" class="clinic-multi-count">
                    · {{ creatorUids.length }} คน ·
                    <button type="button" class="clinic-multi-clear" @click="creatorUids = []">
                      ล้าง
                    </button>
                  </span>
                </label>
                <multiselect
                  class="multiselect-h-36px clinic-multi"
                  :value="selectedCreatorOptions"
                  :options="creatorOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :hide-selected="true"
                  track-by="value"
                  label="text"
                  :placeholder="creatorUids.length ? 'เพิ่มผู้สร้างนัด…' : 'ทุกคน · พิมพ์เพื่อเลือกบางคน'"
                  selectLabel=""
                  selectedLabel=""
                  deselectLabel=""
                  @input="onCreatorsInput"
                >
                  <span slot="noResult">ไม่พบผู้สร้างนัด กรุณาเปลี่ยนคำค้นหา</span>
                  <span slot="noOptions">ยังไม่มีผู้สร้างนัดให้เลือก กด "แสดงข้อมูล" ก่อน</span>
                </multiselect>
              </div>
            </div>
          </div>

          <div v-if="report" class="mt-3">
            <div class="row kpi-row">
              <div class="col-6 col-lg-3 mb-2">
                <div class="kpi">
                  <div class="kpi-lbl">นัดทั้งหมด</div>
                  <div class="kpi-val">{{ fmt(report.summary.totalAppointments) }}</div>
                </div>
              </div>
              <div class="col-6 col-lg-3 mb-2">
                <div class="kpi">
                  <div class="kpi-lbl">
                    นัดแรกของคนไข้ใหม่
                    <i
                      v-b-tooltip.hover
                      class="fas fa-info-circle text-muted"
                      :title="newPatientTooltip"
                    ></i>
                  </div>
                  <div class="kpi-val">{{ fmt(report.summary.newPatientAppointments) }}</div>
                </div>
              </div>
              <div class="col-6 col-lg-3 mb-2">
                <div class="kpi">
                  <div class="kpi-lbl">ช่องทางอันดับ 1 (คนไข้ใหม่)</div>
                  <div v-if="top" class="kpi-val">
                    {{ top.channel }}
                    <small class="text-muted">
                      {{ fmt(top.count) }} · {{ pct(top.pct) }}
                    </small>
                  </div>
                  <div v-else class="kpi-val text-muted">-</div>
                </div>
              </div>
              <div class="col-6 col-lg-3 mb-2">
                <div class="kpi">
                  <div class="kpi-lbl">% ไม่ระบุช่องทาง</div>
                  <div class="kpi-val">
                    {{ report.summary.unknownPct == null ? "-" : pct(report.summary.unknownPct) }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="report.byChannel.length" class="chart-wrap" :style="{ height: chartHeight }">
              <HorizontalBarChart :chart-data="chartData" :options="chartOptions" />
            </div>
            <div v-else class="text-center text-muted p-4">ไม่มีข้อมูลในช่วงที่เลือก</div>
            <div class="table-note">{{ multiChannelNote }}</div>

            <b-tabs class="mt-3" card>
              <b-tab title="แนวโน้ม" active>
                <div class="mb-2">
                  <b-form-radio-group
                    v-model="trendMode"
                    :options="trendModeOptions"
                    buttons
                    button-variant="outline-primary"
                    size="sm"
                  />
                </div>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered report-table">
                    <thead class="thead-light">
                      <tr>
                        <th>ช่วงเวลา</th>
                        <th v-for="c in trendTable.channels" :key="c" class="text-right">
                          {{ c }}
                        </th>
                        <th class="text-right">รวม</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="p in trendTable.periods" :key="p">
                        <td>{{ periodLabel(p) }}</td>
                        <td v-for="c in trendTable.channels" :key="c" class="text-right">
                          {{ fmt(trendTable.cells[p][c]) }}
                        </td>
                        <td class="text-right font-weight-bold">
                          {{ fmt(trendTable.totals.byPeriod[p]) }}
                        </td>
                      </tr>
                      <tr v-if="!trendTable.periods.length">
                        <td :colspan="trendTable.channels.length + 2" class="text-center text-muted">
                          ไม่มีข้อมูล
                        </td>
                      </tr>
                    </tbody>
                    <tfoot v-if="trendTable.periods.length">
                      <tr class="grand-total">
                        <td>รวม</td>
                        <td v-for="c in trendTable.channels" :key="c" class="text-right">
                          {{ fmt(trendTable.totals.byChannel[c]) }}
                        </td>
                        <td class="text-right">{{ fmt(trendTable.totals.grand) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div class="table-note">{{ multiChannelNote }}</div>
              </b-tab>

              <b-tab :title="`รายการนัด (${fmt(rowsTotal)})`">
                <b-table
                  :items="report.rows"
                  :fields="rowFields"
                  small
                  striped
                  responsive
                  show-empty
                  empty-text="ไม่มีข้อมูล"
                >
                  <template #cell(hn)="{ item }">{{ item.hn || "-" }}</template>
                  <template #cell(patientName)="{ item }">{{ item.patientName || "-" }}</template>
                  <template #cell(startDt)="{ item }">{{ dateTime(item.startDt) }}</template>
                  <template #cell(creationDt)="{ item }">{{ dateTime(item.creationDt) }}</template>
                  <template #cell(creatorName)="{ item }">
                    <span v-if="item.creatorName">{{ item.creatorName }}</span>
                    <span v-else class="text-muted">ไม่พบผู้ใช้</span>
                  </template>
                  <template #cell(apptTreatNames)="{ item }">
                    {{ (item.apptTreatNames || []).join(", ") || "-" }}
                  </template>
                  <template #cell(channels)="{ item }">{{ item.channels.join(", ") }}</template>
                  <template #cell(note)="{ item }">
                    <div class="note-cell">{{ item.note }}</div>
                  </template>
                  <template #cell(isNewPatient)="{ item }">
                    <b-badge :variant="item.isNewPatient ? 'primary' : 'light'">
                      {{ item.isNewPatient ? "ใหม่" : "เก่า" }}
                    </b-badge>
                  </template>
                </b-table>
                <b-pagination
                  v-if="rowsTotal > perPage"
                  v-model="currentPage"
                  :total-rows="rowsTotal"
                  :per-page="perPage"
                  size="sm"
                  align="center"
                  @change="changePage"
                />
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </b-card>

      <!--
        Off-screen sources for the Excel export: the on-screen list is paginated and the
        trend tab may be hidden, so the sheets are built from the full data instead.
        Rendered only while an export is in flight.
      -->
      <div v-if="exportSourceReady && report" class="export-source" aria-hidden="true">
        <table id="appointmentSourceChannelTable" class="table table-sm">
          <thead>
            <tr>
              <th class="text-left">ช่องทาง</th>
              <th class="text-right">คนไข้ใหม่</th>
              <th class="text-right">คนไข้เก่า</th>
              <th class="text-right">รวม</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in report.byChannel" :key="c.channel">
              <td class="text-left">{{ c.channel }}</td>
              <td class="text-right">{{ c.newPatient }}</td>
              <td class="text-right">{{ c.returning }}</td>
              <td class="text-right">{{ c.newPatient + c.returning }}</td>
            </tr>
          </tbody>
        </table>

        <table id="appointmentSourceTrendTable" class="table table-sm">
          <thead>
            <tr>
              <th class="text-left">ช่วงเวลา</th>
              <th v-for="c in trendTable.channels" :key="c" class="text-right">{{ c }}</th>
              <th class="text-right">รวม</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in trendTable.periods" :key="p">
              <td class="text-left">{{ periodLabel(p) }}</td>
              <td v-for="c in trendTable.channels" :key="c" class="text-right">
                {{ trendTable.cells[p][c] }}
              </td>
              <td class="text-right">{{ trendTable.totals.byPeriod[p] }}</td>
            </tr>
          </tbody>
          <tfoot v-if="trendTable.periods.length">
            <tr>
              <td class="text-left">รวม</td>
              <td v-for="c in trendTable.channels" :key="c" class="text-right">
                {{ trendTable.totals.byChannel[c] }}
              </td>
              <td class="text-right">{{ trendTable.totals.grand }}</td>
            </tr>
          </tfoot>
        </table>

        <table id="appointmentSourceRowsTable" class="table table-sm">
          <thead>
            <tr>
              <th v-for="h in rowSheetHeaders" :key="h" class="text-left">{{ h }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(cells, i) in exportRows" :key="i">
              <td v-for="(v, j) in cells" :key="j" class="text-left">{{ v }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
      <PdfExport ref="pdfExport" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import HorizontalBarChart from "@/components/chart/HorizontalBarChart";
import ClinicBranchMultiSelect from "@/components/reports/ClinicBranchMultiSelect";
import PdfExport from "@/components/PdfExport";
import { eventBus } from "@/main";
import {
  toTrendTable,
  periodLabel,
  toRowsSheet,
  dateTime,
  collectAllRows,
  rememberCreators,
  mergeCreatorOptions,
  describeFilters,
  ROW_SHEET_HEADERS,
  MULTI_CHANNEL_NOTE,
} from "@/utils/appointmentSourceReport";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

// The list tab shows one server page at a time; the Excel export pulls every page at the
// API's maximum page size.
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 2000;

// Today's calendar date in Bangkok, whatever the browser's timezone is.
const bangkokToday = () => {
  const ymd = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Bangkok" });
  const [y, m, d] = ymd.split("-").map(Number);
  return { y, m, d };
};

export default {
  name: "ClinicAppointmentSourceReport",
  components: { Dialog, Loading, HorizontalBarChart, ClinicBranchMultiSelect, PdfExport },
  data() {
    return {
      isLoading: false,
      dateRange: [],
      dateField: "start_dt",
      groupBy: "week",
      branchIds: [],
      creatorUids: [],
      report: null,
      // What the shown report was fetched with, so labels, paging and the export stay in
      // step with the data even if the filters are changed afterwards.
      reportGroupBy: "week",
      reportRange: [],
      reportParams: null,
      // uid -> name of every creator seen this session (keeps a selected creator nameable
      // after a range change drops them from the response).
      seenCreatorNames: {},
      // Request counters: a response is dropped once a newer report fetch (requestSeq) or
      // a newer page fetch (pageSeq) has started. A report fetch also invalidates pages.
      requestSeq: 0,
      pageSeq: 0,
      isExporting: false,
      exportSourceReady: false,
      exportRowsData: [],
      rowSheetHeaders: ROW_SHEET_HEADERS,
      multiChannelNote: MULTI_CHANNEL_NOTE,
      trendMode: "new",
      currentPage: 1,
      perPage: PAGE_SIZE,
      newPatientTooltip:
        "นัดแรกของคนไข้ ที่ลงทะเบียนไม่เกิน 30 วันก่อนวันนัด รวมนัดที่ยังไม่ลงประวัติ",
      dateFieldOptions: [
        { text: "วันที่นัด", value: "start_dt" },
        { text: "วันที่สร้างนัด", value: "creation_dt" },
      ],
      groupByOptions: [
        { text: "รายวัน", value: "day" },
        { text: "รายสัปดาห์", value: "week" },
        { text: "รายเดือน", value: "month" },
      ],
      trendModeOptions: [
        { text: "นัดแรกของคนไข้ใหม่", value: "new" },
        { text: "นัดทั้งหมด", value: "all" },
      ],
      rowFields: [
        { key: "hn", label: "HN" },
        { key: "patientName", label: "ชื่อ" },
        { key: "startDt", label: "วันที่นัด" },
        { key: "creationDt", label: "วันที่สร้าง" },
        { key: "branchName", label: "สาขา" },
        { key: "creatorName", label: "ผู้สร้าง" },
        { key: "statusName", label: "สถานะ" },
        { key: "apptTreatNames", label: "นัดเพื่อ" },
        { key: "channels", label: "ช่องทาง" },
        { key: "note", label: "หมายเหตุนัด", thStyle: { minWidth: "220px" } },
        { key: "isNewPatient", label: "ใหม่/เก่า" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    exportRows() {
      return toRowsSheet(this.exportRowsData);
    },
    rowsTotal() {
      return (this.report && this.report.pagination && this.report.pagination.total) || 0;
    },
    branchOptions() {
      return ((this.report && this.report.branches) || []).map(b => ({
        value: b.id,
        text: b.name || b.code,
      }));
    },
    creatorOptions() {
      return mergeCreatorOptions(
        this.report && this.report.creators,
        this.creatorUids,
        this.seenCreatorNames,
      ).map(c => ({ value: c.uid, text: c.name }));
    },
    // A selected creator missing from the options (kept by mergeCreatorOptions, so rare)
    // still shows as a chip the user can remove.
    selectedCreatorOptions() {
      return this.creatorUids.map(
        uid => this.creatorOptions.find(o => o.value === uid) || { value: uid, text: String(uid) }
      );
    },
    top() {
      return this.report && this.report.summary.topNewPatientChannel;
    },
    trendTable() {
      return toTrendTable(this.report ? this.report.trend : [], {
        mode: this.trendMode,
        trendTotals: this.report ? this.report.trendTotals : [],
      });
    },
    chartHeight() {
      const n = this.report ? this.report.byChannel.length : 0;
      return `${Math.max(160, n * 36 + 70)}px`;
    },
    chartData() {
      const items = this.report ? this.report.byChannel : [];
      return {
        labels: items.map(i => i.channel),
        datasets: [
          {
            label: "คนไข้ใหม่",
            backgroundColor: "#1d4ed8",
            data: items.map(i => i.newPatient),
          },
          {
            label: "คนไข้เก่า",
            backgroundColor: "#93c5fd",
            data: items.map(i => i.returning),
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: "top" },
        scales: {
          xAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
          yAxes: [{ stacked: true }],
        },
      };
    },
  },
  created() {
    // The PDF exporter lives on this page: exportReportToPdf emits on the bus and the host
    // view renders it, same as the other report screens.
    this.handleExportReportPdf = payload => {
      if (this.$refs.pdfExport) this.$refs.pdfExport.generatePdf(payload);
    };
    eventBus.$on("exportReportPdf", this.handleExportReportPdf);
  },
  beforeDestroy() {
    eventBus.$off("exportReportPdf", this.handleExportReportPdf);
  },
  mounted() {
    // Default range: first day of the current month (Bangkok) through today.
    const { y, m, d } = bangkokToday();
    this.dateRange = [new Date(y, m - 1, 1), new Date(y, m - 1, d)];
    this.fetchReport();
  },
  methods: {
    ...mapActions({
      fetchClinicAppointmentSourceReport:
        "moduleReport/fetchClinicAppointmentSourceReport",
    }),
    fmt(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },
    pct(v) {
      return `${Number(v || 0).toFixed(1)}%`;
    },
    dateTime,
    periodLabel(p) {
      return periodLabel(p, this.reportGroupBy);
    },
    onCreatorsInput(opts) {
      this.creatorUids = (opts || []).map(o => o.value);
    },
    // Header shared by the Excel and PDF exports: the displayed report's range and filters.
    exportMeta() {
      const filters = describeFilters(this.reportParams, {
        branches: this.report.branches,
        creatorNames: this.seenCreatorNames,
      });
      return {
        reportTitle: "รายงานช่องทางนัดหมาย",
        reportNameEn: "appointment-source",
        dateFrom: this.reportRange[0],
        dateTo: this.reportRange[1],
        branchName: filters.branchText,
        clinicName: this.clinicInfo && this.clinicInfo.name,
        extraLines: [filters.line],
      };
    },
    // Summary and trend only: the appointment list runs to thousands of rows, which is not
    // a PDF anyone reads. The list is in the Excel export.
    async exportPdf() {
      if (!this.report || !this.reportParams || this.isExporting) return;
      this.isExporting = true;
      try {
        this.exportSourceReady = true;
        await this.$nextTick();
        const meta = this.exportMeta();
        exportReportToPdf({
          ...meta,
          extraLines: [...meta.extraLines, MULTI_CHANNEL_NOTE],
          autoFit: true,
          sheets: [
            { name: "สรุปตามช่องทาง", tableId: "appointmentSourceChannelTable" },
            { name: "แนวโน้ม", tableId: "appointmentSourceTrendTable" },
          ],
        });
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "ไม่สามารถส่งออก PDF ได้");
      } finally {
        this.exportSourceReady = false;
        this.isExporting = false;
      }
    },
    async exportExcel() {
      if (!this.report || !this.reportParams || this.isExporting) return;
      this.isExporting = true;
      this.isLoading = true;
      const params = this.reportParams;
      try {
        // Same filters and range as the displayed report; every page, one after another.
        // A failed page rejects the whole collection, so no partial file is written.
        const rows = await collectAllRows(
          (page, pageSize) =>
            this.fetchClinicAppointmentSourceReport({ ...params, page, pageSize }),
          EXPORT_PAGE_SIZE,
        );
        // Frozen: up to 20000 rows need no reactivity, only one render.
        this.exportRowsData = Object.freeze(rows);
        // Let the Loading overlay paint before the heavy off-screen render below blocks
        // the main thread: Vue's nextTick is a microtask and gives the browser no frame.
        await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
        this.exportSourceReady = true;
        await this.$nextTick();
        exportTableToExcel({
          ...this.exportMeta(),
          sheets: [
            { name: "สรุปตามช่องทาง", tableId: "appointmentSourceChannelTable" },
            {
              name: "แนวโน้ม",
              tableId: "appointmentSourceTrendTable",
              extraLines: [MULTI_CHANNEL_NOTE],
            },
            { name: "รายการนัด", tableId: "appointmentSourceRowsTable" },
          ],
        });
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "ไม่สามารถส่งออก Excel ได้");
      } finally {
        this.exportSourceReady = false;
        this.exportRowsData = [];
        this.isExporting = false;
        this.isLoading = false;
      }
    },
    async fetchReport() {
      if (!this.dateRange[0] || !this.dateRange[1]) return;
      this.isLoading = true;
      const startDate = moment(this.dateRange[0]).format("YYYY-MM-DD");
      const endDate = moment(this.dateRange[1]).format("YYYY-MM-DD");
      const params = {
        clinicUrl: this.$route.params.clinicUrl,
        startDate,
        endDate,
        dateField: this.dateField,
        groupBy: this.groupBy,
        branchIds: [...this.branchIds],
        creatorUids: [...this.creatorUids],
      };
      const seq = ++this.requestSeq;
      this.pageSeq += 1;
      try {
        const data = await this.fetchClinicAppointmentSourceReport({
          ...params,
          page: 1,
          pageSize: PAGE_SIZE,
        });
        if (seq !== this.requestSeq) return;
        this.report = data;
        this.reportParams = params;
        this.reportGroupBy = params.groupBy;
        this.reportRange = [startDate, endDate];
        this.seenCreatorNames = rememberCreators(this.seenCreatorNames, data.creators);
        this.currentPage = 1;
      } catch (err) {
        if (seq !== this.requestSeq) return;
        const msg =
          err && err.response && err.response.data && err.response.data.message;
        this.$refs.Dialog.showToast("danger", msg || "ไม่สามารถโหลดรายงานได้");
      } finally {
        if (seq === this.requestSeq) this.isLoading = false;
      }
    },
    // List tab paging: re-request the displayed report's page and swap in only its rows
    // and pagination; the aggregates from the main fetch stay as they are.
    async changePage(page) {
      if (!this.report || !this.reportParams) return;
      this.isLoading = true;
      const reportSeq = this.requestSeq;
      const seq = ++this.pageSeq;
      const isCurrent = () => reportSeq === this.requestSeq && seq === this.pageSeq;
      try {
        const data = await this.fetchClinicAppointmentSourceReport({
          ...this.reportParams,
          page,
          pageSize: PAGE_SIZE,
        });
        if (!isCurrent()) return;
        // Set in place: replacing `report` would re-render the chart and the trend table.
        this.$set(this.report, "rows", data.rows);
        this.$set(this.report, "pagination", data.pagination);
      } catch (err) {
        if (!isCurrent()) return;
        this.currentPage = (this.report.pagination && this.report.pagination.page) || 1;
        const msg =
          err && err.response && err.response.data && err.response.data.message;
        this.$refs.Dialog.showToast("danger", msg || "ไม่สามารถโหลดรายการนัดได้");
      } finally {
        if (isCurrent()) this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Filter bar: date, the two toggles at their natural width, actions pushed right. */
.asr-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 16px;
}
.asr-date {
  width: 240px;
}
.asr-row .seg {
  height: 32px;
}
.asr-row .seg-btn {
  height: 24px;
  font-size: 12px;
  white-space: nowrap;
}
.asr-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-left: auto;
}
.asr-actions ::v-deep .dropdown-toggle {
  height: 32px;
  border-radius: 8px;
  padding: 0 12px;
}
.kpi {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  height: 100%;
}
.kpi-lbl {
  font-size: 0.75rem;
  color: #6c757d;
}
.kpi-val {
  font-size: 1.25rem;
  font-weight: 700;
}
.chart-wrap {
  position: relative;
}
.table-note {
  margin: 6px 2px 0;
  font-size: 0.75rem;
  color: #6c757d;
}
.export-source {
  position: absolute;
  left: -10000px;
  top: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.note-cell {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  min-width: 220px;
}
.report-table tfoot tr.grand-total td {
  border-top: 2px solid #adb5bd;
  background: #f8f9fa;
  font-weight: 700;
}
</style>
